/**
 * Leaderboard — Hot Streak ranking.
 * Default: in-app, anonymized for users who haven't opted-in to public.
 */
import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Flame, Trophy, ShieldCheck, Eye, EyeOff, Loader2, ExternalLink, Globe,
} from "lucide-react";

const WINDOWS = [
  { days: 7,  label: "7d"  },
  { days: 30, label: "30d" },
  { days: 90, label: "90d" },
];

const RANK_BG = {
  1: "linear-gradient(120deg, #F59E0B 0%, #FB923C 35%, #EF4444 100%)",
  2: "linear-gradient(120deg, #94A3B8 0%, #CBD5E1 100%)",
  3: "linear-gradient(120deg, #92400E 0%, #B45309 100%)",
};

export default function Leaderboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(30);
  const [pref, setPref] = useState(null);
  const [prefBusy, setPrefBusy] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const [board, p] = await Promise.all([
        api.get("/leaderboard/streaks", { params: { days } }),
        api.get("/leaderboard/preferences"),
      ]);
      setData(board.data);
      setPref(p.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't load leaderboard");
    } finally {
      setLoading(false);
    }
  }, [days]);

  useEffect(() => { refresh(); }, [refresh]);

  const togglePublic = async () => {
    setPrefBusy(true);
    try {
      const { data: d } = await api.put("/leaderboard/preferences", {
        leaderboard_public: !pref?.leaderboard_public,
      });
      setPref(d);
      toast.success(
        d.leaderboard_public
          ? "You're now showing on the public leaderboard"
          : "Removed from the public leaderboard",
      );
      refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't update preference");
    } finally {
      setPrefBusy(false);
    }
  };

  return (
    <div data-testid="leaderboard-page">
      <div className="topbar">
        <div>
          <div className="crumb">Community</div>
          <h1 className="font-display font-extrabold text-3xl tracking-tight mt-1 flex items-center gap-2">
            <Flame size={24} className="text-orange-500" /> Hot Streak Leaderboard
          </h1>
          <p className="text-sm text-[var(--ink-3)] mt-1 max-w-2xl">
            Top wholesalers by buyer reply rate. Only first names are shown unless someone's opted into the public board.
            Need at least <strong>{data?.min_to_qualify ?? 10}</strong> nudges in window to qualify.
          </p>
        </div>
        <div className="flex gap-2">
          {WINDOWS.map((w) => (
            <button
              key={w.days}
              type="button"
              className={"btn " + (days === w.days ? "btn-primary" : "btn-ghost")}
              onClick={() => setDays(w.days)}
              data-testid={`leaderboard-window-${w.days}`}
            >
              {w.label}
            </button>
          ))}
        </div>
      </div>

      {/* Opt-in / privacy bar */}
      <div
        className={"card p-4 mb-4 flex items-start gap-3 " + (pref?.leaderboard_public ? "border-l-4 border-l-orange-500" : "")}
        data-testid="leaderboard-privacy-card"
      >
        <div className={"w-9 h-9 rounded-full flex items-center justify-center shrink-0 " + (pref?.leaderboard_public ? "bg-orange-100" : "bg-slate-100")}>
          {pref?.leaderboard_public
            ? <Globe size={16} className="text-orange-600" />
            : <ShieldCheck size={16} className="text-slate-600" />}
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-bold text-sm">
            {pref?.leaderboard_public ? "Showing on the public leaderboard" : "You're private by default"}
          </div>
          <p className="text-xs text-[var(--ink-3)] mt-1 leading-relaxed">
            {pref?.leaderboard_public
              ? "Your full name + brand are visible on the public Hot Streak page. Other iSpeedToClose users see you here. Raw send / reply counts are never exposed publicly — only the reply-rate %."
              : "In-app, you appear by first name + metro. The public-facing leaderboard hides you completely. Flip the switch to use your reply rate as social proof."}
          </p>
          <div className="flex items-center gap-3 mt-3">
            <button
              type="button"
              onClick={togglePublic}
              disabled={prefBusy}
              className={"btn text-sm " + (pref?.leaderboard_public ? "btn-ghost" : "btn-primary")}
              data-testid="leaderboard-toggle-public"
            >
              {prefBusy
                ? <Loader2 size={13} className="animate-spin" />
                : pref?.leaderboard_public ? <EyeOff size={13} /> : <Eye size={13} />}
              {pref?.leaderboard_public ? "Make me private again" : "Show me publicly"}
            </button>
            <a
              href="/leaderboards/streaks"
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs text-[var(--ink-3)] hover:text-[var(--primary)] flex items-center gap-1"
              data-testid="leaderboard-view-public-link"
            >
              View public leaderboard <ExternalLink size={11} />
            </a>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="card p-12 text-center text-[var(--ink-3)]">
          <Loader2 size={20} className="inline animate-spin" /> Loading rankings…
        </div>
      ) : (data?.leaderboard?.length ?? 0) === 0 ? (
        <EmptyState selfRow={data?.self_row} min={data?.min_to_qualify} />
      ) : (
        <div className="card p-0 overflow-hidden" data-testid="leaderboard-table">
          {data.leaderboard.map((row) => <Row key={row.rank} row={row} />)}
        </div>
      )}

      {/* Self status when not in top-N */}
      {data?.self_row && !data.self_row.in_top_n && (
        <div className="card mt-4 p-4 border-l-4 border-l-[var(--primary)]" data-testid="leaderboard-self-status">
          {data.self_row.qualified ? (
            <>
              <div className="text-xs uppercase tracking-wider text-[var(--ink-3)]">Your rank</div>
              <div className="font-display font-extrabold text-2xl mt-1">
                #{data.self_row.rank}
                <span className="text-sm font-medium text-[var(--ink-2)] ml-2">of {data.total_qualified}</span>
              </div>
              <div className="text-sm text-[var(--ink-2)] mt-1">
                {data.self_row.reply_rate}% reply rate ({data.self_row.replied} of {data.self_row.sent})
              </div>
            </>
          ) : (
            <>
              <div className="text-xs uppercase tracking-wider text-[var(--ink-3)]">Not yet qualified</div>
              <div className="text-sm mt-1">
                You've sent <strong>{data.self_row.sent}</strong> nudge{data.self_row.sent === 1 ? "" : "s"} in this window.
                Send <strong>{Math.max(0, (data?.min_to_qualify ?? 10) - data.self_row.sent)}</strong> more
                to qualify for the leaderboard.
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

function Row({ row }) {
  const rankBg = RANK_BG[row.rank] || "transparent";
  const isPodium = row.rank <= 3;
  return (
    <div
      className={"flex items-center gap-4 px-5 py-4 border-b border-[var(--line)] last:border-b-0 " + (row.is_self ? "bg-emerald-50/60" : "")}
      data-testid={`leaderboard-row-${row.rank}`}
    >
      <div
        className={"w-10 h-10 rounded-full flex items-center justify-center font-extrabold shrink-0 " + (isPodium ? "text-white" : "bg-slate-100 text-slate-700")}
        style={isPodium ? { background: rankBg } : undefined}
      >
        {isPodium ? <Trophy size={15} fill="currentColor" /> : row.rank}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-bold truncate">{row.display_name}</span>
          {row.company && (
            <span
              className="text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full text-white"
              style={{ background: row.brand_accent || "#0B132B" }}
            >
              {row.company}
            </span>
          )}
          {row.is_self && (
            <span className="text-[10px] uppercase tracking-wider font-bold bg-emerald-100 text-emerald-800 px-2 py-0.5 rounded-full">
              You
            </span>
          )}
          {!row.is_public && !row.is_self && (
            <span className="text-[10px] uppercase tracking-wider font-medium text-[var(--ink-3)] flex items-center gap-0.5">
              <ShieldCheck size={9} /> private
            </span>
          )}
        </div>
        <div className="text-xs text-[var(--ink-3)] mt-0.5">
          {row.metro || "—"}
          {typeof row.replied === "number" && (
            <> · {row.replied} of {row.sent} replies</>
          )}
        </div>
      </div>
      <div className="shrink-0 text-right">
        <div className="font-display font-extrabold text-2xl tabular-nums text-[var(--primary)]">
          {row.reply_rate}%
        </div>
        <div className="text-[10px] uppercase tracking-wider text-[var(--ink-3)]">
          reply rate
        </div>
      </div>
    </div>
  );
}

function EmptyState({ selfRow, min }) {
  return (
    <div className="card p-12 text-center" data-testid="leaderboard-empty">
      <Flame size={32} className="mx-auto text-orange-400 mb-3" />
      <div className="font-display font-bold text-xl mb-1">No qualifying agents yet this window</div>
      <p className="text-sm text-[var(--ink-3)] max-w-md mx-auto">
        Need at least <strong>{min ?? 10}</strong> nudges sent (and replies coming back) to land on this board.
      </p>
      {selfRow && (
        <div className="text-xs text-[var(--ink-2)] mt-3">
          You've sent <strong>{selfRow.sent}</strong> · need <strong>{Math.max(0, (min ?? 10) - selfRow.sent)}</strong> more.
        </div>
      )}
    </div>
  );
}
